/* eslint-disable max-len */
import ExistAccount from '@brands/Auth/SignIn/ExistingAccount';
import PasswordlessSignIn from '@brands/Auth/PasswordlessSignIn/PasswordlessSignIn';
import PasswordlessLinkVerify from '@brands/Auth/PasswordlessLinkVerify/PasswordlessLinkVerify';
import SignIn from '@brands/Auth/SignIn/SignIn';
import AffiliatedPracticeList from '@brands/Auth/SignUp/AffiliatedPracticeList';
import CompleteSignUp from '@brands/Auth/SignUp/CompleteSignUp';
import ConsentPolicy from '@brands/Auth/SignUp/ConsentPolicy';
import NoticeOfPrivacyPractices from '@brands/Auth/SignUp/NoticeOfPrivacyPractices';
import PrivacyPolicy from '@brands/Auth/SignUp/PrivacyPolicy';
import SignUp from '@brands/Auth/SignUp/SignUp';
import TermsOfService from '@brands/Auth/SignUp/TermsOfService';
import WashingtonMyHealthMyDataPrivacyNotice from '@brands/Auth/SignUp/WashingtonMyHealthMyDataPrivacyNotice';
import ZendeskLogout from '@brands/Auth/ZendeskLogout/ZendeskLogout';
import PrivateRoutes from '@brands/Components/PrivateRoutes/PrivateRoutes';
import { config } from '@brands/config/config';
import { VideoProvider } from '@brands/contexts/VideoProvider';
import { useAppSelector } from '@brands/hooks/useReduxHook';
import { UserRoleName } from '@brands/services/identity/types/UserProfile';
import { selectAuth } from '@brands/store/selectors/auth';
import Appointments from '@brands/Dashboard/Appointments/Appointments';
import AddAdditionalInformation from '@brands/Dashboard/Appointments/Patient/AddAdditionalInformation/AddAdditionalInformationValidation';
import CaseChatWindowCheck from '@brands/Dashboard/CaseChatWindow/CaseChatWindowCheck';
import PatientMessages from '@brands/Dashboard/CaseChatWindow/PatientMessages';
import CaseActivityPage from '@brands/Dashboard/ConsultationPage/Modals/CaseActivityModal/CaseActivityPage';
import ConsultationPage from '@brands/Dashboard/ConsultationPage/ConsultationPageValidation';
import OralHealthCoachingReportComponent from '@brands/Dashboard/ConsultationPage/OralHealthCoachingReport/OralHealthCoachingReportValidation';
import OralHealthReportComponent from '@brands/Dashboard/ConsultationPage/OralHealthReport/OralHealthReportValidation';
import SmileScanDisclaimer from '@brands/Dashboard/ConsultationPage/SmileScanReport/SmileScanDisclaimer';
import SmileScanReportComponent from '@brands/Dashboard/ConsultationPage/SmileScanReport/SmileScanReport';
import AdminDashboard from '@brands/Dashboard/Dashboard/AdminDashboard';
import AdminPatientSelect from '@brands/Dashboard/Dashboard/SuperAdmin/PatientSelect/AdminPatientSelect';
import CareAdvisorLayout from '@brands/Dashboard/Dashboard/CareAdvisor/CareAdvisorLayout';
import RequireAdminRole from '@brands/Components/RequireAdminRole/RequireAdminRole';
import Dashboard from '@brands/Dashboard/Dashboard/Dashboard';
import {
  UserDetailRouteByRole,
  UsersNewRouteByRole,
  UsersRouteByRole,
} from '@brands/Dashboard/Dashboard/RoleBasedUserRoutes';
import ScheduleAvailability from '@brands/Dashboard/Dashboard/ScheduleAvailability';
import Insurance from '@brands/Dashboard/Insurance/Insurance';
import DentalHistory from '@brands/Dashboard/MedicalHistory/DentalHistory/DentalHistoryValidation';
import MedicalCondition from '@brands/Dashboard/MedicalHistory/MedicalCondition/MedicalConditionValidation';
import MedicalHistory from '@brands/Dashboard/MedicalHistory/MedicalHistory/MedicalHistoryValidation';
import MedicalInformationSummary from '@brands/Dashboard/MedicalHistory/MedicalInformationSummary/MedicalInformationSummaryValidation';
import LinkedCareCoordinationForm from '@brands/Dashboard/MultiStage/LinkedCareCoordinationForm';
import MyCare from '@brands/Dashboard/MyCare/MyCare';
import MyCareJourney from '@brands/Dashboard/MyCare/MyCareJourney';
import MyProfile from '@brands/Dashboard/MyProfile/Myprofile';
import CreateConsultationFor from '@brands/Dashboard/MyProfile/ReviewProfile/CreateConsultationForValidation';
import ReviewProfile from '@brands/Dashboard/MyProfile/ReviewProfile/ReviewProfile';
import NotFoundPage from '@brands/Dashboard/NotFoundPage/NotFoundPage';
import AdditionalInformation from '@brands/Dashboard/PatientForms/PatientForm/AdditionalInformation/AdditionalInformationValidation';
import ClinicalDataIntro from '@brands/Dashboard/PatientForms/PatientForm/ClinicalData/ClinicalDataIntro/ClinicalDataIntroValidation';
import ClinicalDataStepEight from '@brands/Dashboard/PatientForms/PatientForm/ClinicalData/ClinicalDataStepEight/ClinicalDataStepEight';
import ClinicalDataStepFive from '@brands/Dashboard/PatientForms/PatientForm/ClinicalData/ClinicalDataStepFive/ClinicalDataStepFiveValidation';
import ClinicalDataStepFour from '@brands/Dashboard/PatientForms/PatientForm/ClinicalData/ClinicalDataStepFour/ClinicalDataStepFourValidation';
import ClinicalDataStepNine from '@brands/Dashboard/PatientForms/PatientForm/ClinicalData/ClinicalDataStepNine/ClinicalDataStepNine';
import ClinicalDataStepOne from '@brands/Dashboard/PatientForms/PatientForm/ClinicalData/ClinicalDataStepOne/ClinicalDataStepOneValidation';
import ClinicalDataStepSeven from '@brands/Dashboard/PatientForms/PatientForm/ClinicalData/ClinicalDataStepSeven/ClinicalDataStepSeven';
import ClinicalDataStepSix from '@brands/Dashboard/PatientForms/PatientForm/ClinicalData/ClinicalDataStepSix/ClinicalDataStepSix';
import ClinicalDataStepThree from '@brands/Dashboard/PatientForms/PatientForm/ClinicalData/ClinicalDataStepThree/ClinicalDataStepThreeValidation';
import ClinicalDataStepTwo from '@brands/Dashboard/PatientForms/PatientForm/ClinicalData/ClinicalDataStepTwo/ClinicalDataStepTwoValidation';
import CreateYourProfile from '@brands/Dashboard/PatientForms/PatientForm/CreateYourProfile/CreateYourProfileValidation';
import DescribePainLevel from '@brands/Dashboard/PatientForms/PatientForm/DescribePainLevel/DescribePainLevelValidation';
import ImageCapture from '@brands/Dashboard/PatientForms/PatientForm/ImageCapture/ImageCaptureValidation';
import ImageUploadProcessValidation from '@brands/Dashboard/PatientForms/PatientForm/ImageUpload/ImageUploadProcessValidation';
import ImageUploadStepFiveValidation from '@brands/Dashboard/PatientForms/PatientForm/ImageUpload/ImageUploadStepFiveValidation';
import ImageUploadStepFourValidation from '@brands/Dashboard/PatientForms/PatientForm/ImageUpload/ImageUploadStepFourValidation';
import ImageUploadStepOneValidation from '@brands/Dashboard/PatientForms/PatientForm/ImageUpload/ImageUploadStepOneValidation';
import ImageUploadStepThreeValidation from '@brands/Dashboard/PatientForms/PatientForm/ImageUpload/ImageUploadStepThreeValidation';
import ImageUploadStepTwoValidation from '@brands/Dashboard/PatientForms/PatientForm/ImageUpload/ImageUploadStepTwoValidation';
import ImageUpload from '@brands/Dashboard/PatientForms/PatientForm/ImageUpload/ImageUploadValidation';
import QRLearnMore from '@brands/Dashboard/PatientForms/PatientForm/ImageUpload/QRLearnMore';
import ConfirmPayment from '@brands/Dashboard/PatientForms/PatientForm/PaymentInformation/ConfirmPaymentRoute';
import PaymentInformation from '@brands/Dashboard/PatientForms/PatientForm/PaymentInformation/PaymentInformationValidation';
import SelectAppointmentType from '@brands/Dashboard/PatientForms/PatientForm/SelectAppointmentType/SelectAppointmentTypeValidation';
import SelectCareAdvisor from '@brands/Dashboard/PatientForms/PatientForm/SelectCareAdvisor/SelectCareAdvisorValidation';
import SelectCareAdvisorDetails from '@brands/Dashboard/PatientForms/PatientForm/SelectCareAdvisorDetails/SelectCareAdvisorDetailsValidation';
import SelectCaseType from '@brands/Dashboard/PatientForms/PatientForm/SelectCaseType/SelectCaseType';
import SelectDateAndTime from '@brands/Dashboard/PatientForms/PatientForm/SelectDateAndTime/SelectDateAndTimeValidation';
import SelectDentaInsurance from '@brands/Dashboard/PatientForms/PatientForm/SelectDentaInsurance/SelectDentaInsuranceValidation';
import SelectNeeds from '@brands/Dashboard/PatientForms/PatientForm/SelectNeeds/SelectNeedsValidation';
import PatientClinicalEnablement from '@brands/Dashboard/PatientForms/CaseCreation/PatientClinicalEnablement';
import AddNewDependent from '@brands/Dashboard/PatientForms/PatientForm/SelectPatient/AddNewDependent';
import SelectPatient from '@brands/Dashboard/PatientForms/PatientForm/SelectPatient/SelectPatientValidation';
import SelectPharmacy from '@brands/Dashboard/PatientForms/PatientForm/SelectPharmacy/SelectPharmacyRoute';
import SelectProvider from '@brands/Dashboard/PatientForms/PatientForm/SelectProvider/SelectProviderValidation';
import SelectProviderDetails from '@brands/Dashboard/PatientForms/PatientForm/SelectProviderDetails/SelectProviderDetailsRoute';
import VerifyInsurance from '@brands/Dashboard/PatientForms/PatientForm/VerifyInsurance/VerifyInsuranceValidation';
import PatientPlan from '@brands/Dashboard/PatientPlan/PatientPlan';
import PaymentPage from '@brands/Dashboard/PaymentPage/PaymentPageValidation';
import Pharmacy from '@brands/Dashboard/Pharmacy/Pharmacy';
import SmileScan from '@brands/Dashboard/SmileScan/SmileScan';
import Solutions from '@brands/Dashboard/Solutions/SolutionsValidation';
import useConnectionOptions from '@brands/hooks/video-hooks/useConnectionOptions';
import ProviderConfirmPage from '@brands/ProviderConfirmation/ProviderConfirmPage';
import ProviderDeclinePage from '@brands/ProviderConfirmation/ProviderDeclinePage';
import VideoRoom from '@brands/VideoRoom/VideoRoom/VideoRoom';
import React, { FC, useEffect } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ConnectOptions } from 'twilio-video';

import history from '../history';

function shouldSuppressZendeskFloatingWidget(pathname: string): boolean {
  const segments = pathname.split('/').filter(Boolean);
  if (segments[0]?.toLowerCase() === 'careadvisor') return true;
  if (segments.some((s) => s.toLowerCase() === 'case-chat')) return true;
  if (segments.some((s) => s.toLowerCase() === 'messages')) return true;
  return false;
}

function setZendeskFloatingWidgetVisible(visible: boolean): void {
  if (typeof window === 'undefined') return;
  const zE = (window as Window & { zE?: (...args: unknown[]) => void }).zE;
  if (typeof zE !== 'function') return;
  try {
    zE('webWidget', visible ? 'show' : 'hide');
  } catch {
    try {
      zE('messenger', visible ? 'show' : 'hide');
    } catch {
      /* widget API not available */
    }
  }
}

// Canonicalize only the role prefix (the first path segment) to lowercase.
// Role names are PascalCase (e.g. `Patient`, `CareAdvisor`) and navigation
// builds paths as `/${role.name}/...`, so that leading segment is the only one
// that can arrive mixed-case. Deeper segments are authored lowercase or are
// case-sensitive dynamic values (confirmation tokens, conversation SIDs) that
// must never be altered — lowercasing an all-letter token would corrupt it.
function normalizePath(pathname: string): string {
  const segments = pathname.split('/');
  // segments[0] is '' for a leading-slash path; the role prefix follows it.
  const roleIndex = segments[0] === '' ? 1 : 0;
  const role = segments[roleIndex];
  if (role && /^[A-Za-z-]+$/.test(role)) {
    segments[roleIndex] = role.toLowerCase();
  }
  return segments.join('/');
}

const AdminOrDefaultDashboard: FC = () => {
  const { userInfo } = useAppSelector(selectAuth);
  if (
    userInfo?.role.name === UserRoleName.Admin ||
    userInfo?.role.name === UserRoleName.SuperAdmin ||
    userInfo?.role.name === UserRoleName.OrganizationAdmin
  ) {
    return <AdminDashboard />;
  }
  return <Dashboard />;
};

/** Permanent redirect from legacy collector URLs to `/collector/case-creation/...`. */
const LegacyCollectorReviewProfileRedirect: FC = () => {
  const { pathname, search, hash } = useLocation();
  const rest = pathname.replace(/^\/collector\/review-profile/, '') || '';
  return <Navigate to={`/collector/case-creation${rest}${search}${hash}`} replace />;
};

/**
 * Keys the video room subtree by caseId so navigating between two
 * `video-room/:caseId` URLs (e.g. one appointment link to another) fully
 * remounts VideoProvider/VideoRoom/WaitingRoom instead of leaving stale
 * per-case state (and a stale case id passed to join_video_room) behind.
 */
const VideoRoomRoute: FC<{ options: ConnectOptions }> = ({ options }) => {
  const { caseId } = useParams();
  return (
    <VideoProvider key={caseId} options={options}>
      <VideoRoom />
    </VideoProvider>
  );
};

const Routing: FC = () => {
  const connectionOptions: ConnectOptions = useConnectionOptions();
  const location = useLocation();
  const navigate = useNavigate();

  const dynamicPath = '/:userRole/*';

  // Redirect mixed-case URLs (e.g. /Patient/dashboard) to their lowercase
  // equivalents so every role-prefixed path has a single canonical form.
  // Only pure-letter/hyphen segments are lowercased; numeric IDs and tokens
  // that contain digits are preserved unchanged.
  useEffect(() => {
    const normalized = normalizePath(location.pathname);
    if (normalized !== location.pathname) {
      navigate(normalized + location.search + location.hash, { replace: true, state: location.state });
    }
  }, [location.pathname, location.search, location.hash, navigate]);

  // Fire GA page_view only after the URL is already in its normalized form
  // to prevent double-counting when the redirect above fires first.
  useEffect(() => {
    if (normalizePath(location.pathname) !== location.pathname) return;
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_path: location.pathname + location.search,
        page_title: document.title,
      });
    }
  }, [location]);

  useEffect(() => {
    setZendeskFloatingWidgetVisible(!shouldSuppressZendeskFloatingWidget(location.pathname));
  }, [location.pathname]);

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<SignIn />} />
      {config.features.passwordless && <Route path="/sign-in/code" element={<PasswordlessSignIn />} />}
      {config.features.passwordless && <Route path="/sign-in/link" element={<PasswordlessLinkVerify />} />}
      <Route path="/sign-up/*" element={<SignUp />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      <Route path="/notice-of-privacy-practices" element={<NoticeOfPrivacyPractices />} />
      <Route path="/washington_my_health_my_data_privacy_notice" element={<WashingtonMyHealthMyDataPrivacyNotice />} />
      <Route path="/consent-policy" element={<ConsentPolicy />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/affiliated-practice-list" element={<AffiliatedPracticeList />} />
      <Route path="/smile-scan-disclaimer" element={<SmileScanDisclaimer />} />
      <Route path="/complete-signup" element={<CompleteSignUp />} />
      <Route path="/existing-account" element={<ExistAccount />} />
      <Route path="/kare-mobile/sign-up/*" element={<SignUp />} />
      <Route path="/kare-mobile/login" element={<SignIn />} />
      {/* Bare legacy paths that PrivateRoutes.tsx's own initialize() detects by
          exact pathname and role-redirects (e.g. /dashboard -> /patient/dashboard).
          They only ever reached PrivateRoutes because react-router treats the
          trailing /* in dynamicPath ("/:userRole/*") as optional, matching
          /dashboard with userRole="dashboard" and an empty splat — a match
          /:clientCode below would otherwise win on ranking (no splat penalty),
          swallowing these two before PrivateRoutes ever sees them. Static routes
          always outrank a dynamic /:clientCode sibling, so these stay literal. */}
      <Route path="/dashboard" element={<PrivateRoutes />} />
      <Route path="/my-profile" element={<PrivateRoutes />} />
      {/* Same reasoning as /dashboard and /my-profile above: patient
          notification emails link to these bare paths, and without a
          literal route here they fall through to /:clientCode below and
          land on the sign-in screen instead of PrivateRoutes's generic
          role-prepend redirect (or its redirect_uri handoff through sign-in
          when the session has expired). */}
      <Route path="/my-care" element={<PrivateRoutes />} />
      {config.features.multiStage && <Route path="/my-care-journey" element={<PrivateRoutes />} />}
      {/* Client-coded landing links (dentistry.one/{client-code}[/sign-up]) — a
          static sibling route always outranks these regardless of declaration
          order (react-router v6 ranks by segment specificity), so this can't
          shadow any route above or below it. */}
      <Route path="/:clientCode" element={<SignIn />} />
      <Route path="/:clientCode/sign-up/*" element={<SignUp />} />
      <Route path="/admin/login" element={<SignIn />} />
      <Route path="/zendesk_logout" element={<ZendeskLogout />} />

      {/* Provider Confirmation Routes */}
      <Route path="/provider/confirm/:confirmationToken" element={<ProviderConfirmPage />} />
      <Route path="/provider/decline/:confirmationToken" element={<ProviderDeclinePage />} />

      <Route path="*" element={<NotFoundPage />} />

      <Route path="/collector/case-creation/patient/select" element={<AdminPatientSelect mode="collector" />} />
      <Route path="/collector/case-creation/:role/:userId" element={<ReviewProfile />} />
      <Route path="/collector/case-creation/:role/:userId/create-consultation-for" element={<CreateConsultationFor />} />
      <Route path="/careadvisor/case-creation" element={<Navigate to="/careadvisor/case-creation/patient/select" replace />} />
      <Route path="/careadvisor/case-creation/patient/select" element={<AdminPatientSelect mode="collector" />} />
      <Route path="/careadvisor/case-creation/:role/:userId" element={<ReviewProfile />} />
      <Route path="/careadvisor/case-creation/:role/:userId/create-consultation-for" element={<CreateConsultationFor />} />
      <Route path="/collector/review-profile/*" element={<LegacyCollectorReviewProfileRedirect />} />
      <Route path="/:role/my-profile" element={<MyProfile />} />

      {/* Dynamic Role-Based Routes */}
      <Route path={dynamicPath} element={<PrivateRoutes />}>
        {/* Admin-only routes — RequireAdminRole blocks any non-admin role at the router level */}
        <Route element={<RequireAdminRole />}>
          <Route path="review-profile/patient/select" element={<AdminPatientSelect mode="collector" />} />
          <Route path="review-profile/:targetRole/:userId" element={<ReviewProfile />} />
          <Route path="review-profile/:targetRole/:userId/create-consultation-for" element={<CreateConsultationFor />} />
        </Route>

        {/*
          CareAdvisorLayout: global Sidenav + Sidebar + Container for Care Advisor; plain <Outlet /> for other roles.
          All role-shared paths are declared once here (no duplicate Route entries).
        */}
        <Route element={<CareAdvisorLayout />}>
          <Route path="dashboard" element={<AdminOrDefaultDashboard />} />
          <Route path="users" element={<UsersRouteByRole />} />
          <Route path="users/new" element={<UsersNewRouteByRole />} />
          <Route path="users/:userId" element={<UserDetailRouteByRole />} />
          <Route path="patient-select" element={<AdminPatientSelect mode="collector" />} />
          <Route path="select-patient" element={<SelectPatient />} />
          <Route path="clinical-enablement" element={<PatientClinicalEnablement />} />
          <Route path="add-dependent" element={<AddNewDependent />} />
          <Route path="my-profile" element={<MyProfile />} />
          <Route path="smile-scan" element={<SmileScan />} />
          <Route path="scheduleAvailability" element={<ScheduleAvailability />} />
          <Route path="verify-insurance" element={<VerifyInsurance />} />
          <Route path="appointments" element={<Appointments />} />
          <Route path="consultation-page/:caseId/case-activity" element={<CaseActivityPage />} />
          <Route path="consultation-page" element={<ConsultationPage />} />
          <Route path="consultation-page/:caseId" element={<ConsultationPage />} />
          <Route path="smile-scan-report/:caseId" element={<SmileScanReportComponent />} />
          <Route path="my-care" element={<MyCare />} />
          {config.features.multiStage && (
            <Route path="my-care-journey" element={<MyCareJourney />} />
          )}
          {config.features.multiStage && (
            <Route path="care-coordination-form/:caseId" element={<LinkedCareCoordinationForm />} />
          )}
          <Route path="messages" element={<PatientMessages />} />
          <Route path="messages/:conversationSid" element={<PatientMessages />} />
          <Route path="solutions" element={<Solutions />} />
          <Route path="dental-history" element={<DentalHistory />} />
          <Route path="medical-condition" element={<MedicalCondition />} />
          <Route path="medical-history" element={<MedicalHistory />} />
          <Route path="medical-information-summary" element={<MedicalInformationSummary />} />

          <Route path="clinical-data" element={<ClinicalDataIntro />} />
          <Route path="clinical-data/step-one" element={<ClinicalDataStepOne />} />
          <Route path="clinical-data/step-two" element={<ClinicalDataStepTwo />} />
          <Route path="clinical-data/step-three" element={<ClinicalDataStepThree />} />
          <Route path="clinical-data/step-four" element={<ClinicalDataStepFour />} />
          <Route path="clinical-data/step-five" element={<ClinicalDataStepFive />} />
          <Route path="clinical-data/step-six" element={<ClinicalDataStepSix />} />
          <Route path="clinical-data/step-seven" element={<ClinicalDataStepSeven />} />
          <Route path="clinical-data/step-eight" element={<ClinicalDataStepEight />} />
          <Route path="clinical-data/end-step" element={<ClinicalDataStepNine />} />
          <Route path="clinical-data/recap" element={<MedicalHistory />} />
          <Route path="select-pharmacy" element={<SelectPharmacy />} />
          <Route path="payment-page" element={<PaymentPage />} />
          <Route path="insurance" element={<Insurance />} />
          <Route path="select-dental-insurance" element={<SelectDentaInsurance />} />
          <Route path="select-date-time" element={<SelectDateAndTime />} />
          <Route path="select-provider" element={<SelectProvider />} />
          <Route path="select-care-advisor" element={<SelectCareAdvisor />} />
          <Route path="select-provider-details" element={<SelectProviderDetails />} />
          <Route path="select-care-advisor-details" element={<SelectCareAdvisorDetails />} />
          <Route path="select-appointment-type" element={<SelectAppointmentType />} />
          <Route path="select-needs" element={<SelectNeeds />} />
          <Route path="describe-pain-level" element={<DescribePainLevel />} />
          <Route path="additional-information" element={<AdditionalInformation />} />
          <Route path="payment-information/*" element={<PaymentInformation />} />
          <Route path="confirm-payment/*" element={<ConfirmPayment />} />
          <Route path="select-case-type" element={<SelectCaseType />} />
          <Route path="create-your-profile" element={<CreateYourProfile />} />
          <Route path="upload-image" element={<ImageUpload />} />
          <Route path="upload-image-flow" element={<ImageUploadProcessValidation />} />
          <Route path="upload-image-flow-step-one" element={<ImageUploadStepOneValidation />} />
          <Route path="upload-image-flow-step-two" element={<ImageUploadStepTwoValidation />} />
          <Route path="upload-image-flow-step-three" element={<ImageUploadStepThreeValidation />} />
          <Route path="upload-image-flow-step-four" element={<ImageUploadStepFourValidation />} />
          <Route path="upload-image-flow-step-five" element={<ImageUploadStepFiveValidation />} />
          <Route path="smile-scan-link" element={<ImageCapture />} />
          <Route path="case-chat" element={<CaseChatWindowCheck />} />
          <Route path="case-chat/:caseId" element={<CaseChatWindowCheck />} />
          <Route path="case-chat/:caseId/:conversationSid" element={<CaseChatWindowCheck />} />
          <Route path="oral-health-report/:caseId" element={<OralHealthReportComponent />} />
          <Route path="oral-health-coaching-report/:caseId" element={<OralHealthCoachingReportComponent />} />
          <Route path="add-additional-information/:caseId" element={<AddAdditionalInformation />} />
          <Route path="pharmacy" element={<Pharmacy />} />
          <Route path="my-plan" element={<PatientPlan />} />
          <Route path="qr-learn-more" element={<QRLearnMore />} />
          <Route path="video-room/:caseId" element={<VideoRoomRoute options={connectionOptions} />} />
        </Route>
      </Route>
    </Routes>
  );
};

export default Routing;
